import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./VisionBootSequence.css";

const STEPS = [
  {
    key: "init",
    label: "Initializing your Vision",
    detail: "Reserving canvas, allocating categories",
  },
  {
    key: "dashboard",
    label: "Generating Vision dashboard",
    detail: "Wiring nodes, updates, and timelines",
  },
  {
    key: "space",
    label: "Activating Space View",
    detail: "Plotting your categories in 3D",
  },
  {
    key: "health",
    label: "Opening the timeline",
    detail: "Ready for your first record when you are",
  },
  {
    key: "assist",
    label: "Enabling AI powered Dmyor Assist",
    detail: "Strategist, Mirror, Challenger, Therapist",
  },
  {
    key: "live",
    label: "Vision is live",
    detail: "Stepping you in",
  },
];

const STEP_MS = 2000;
const FINAL_HOLD_MS = 900;
const EXIT_MS = 800;

/* ── Per-step center glyphs ── */
const Glyph = ({ stepKey }) => {
  const common = {
    width: 140,
    height: 140,
    viewBox: "0 0 140 140",
    fill: "none",
    stroke: "#111",
    strokeWidth: 1.25,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  switch (stepKey) {
    case "init":
      return (
        <svg {...common} className="vbs-glyph vbs-g-init">
          <rect x="20" y="20" width="100" height="100" className="vbs-g-rect-1" />
          <rect x="40" y="40" width="60" height="60" className="vbs-g-rect-2" />
          <rect x="60" y="60" width="20" height="20" className="vbs-g-rect-3" fill="#111" />
        </svg>
      );

    case "dashboard":
      return (
        <svg {...common} className="vbs-glyph vbs-g-dash">
          <line x1="70" y1="70" x2="30" y2="35" className="vbs-g-line" />
          <line x1="70" y1="70" x2="115" y2="40" className="vbs-g-line vbs-g-line-d2" />
          <line x1="70" y1="70" x2="35" y2="110" className="vbs-g-line vbs-g-line-d3" />
          <line x1="70" y1="70" x2="110" y2="115" className="vbs-g-line vbs-g-line-d4" />
          <circle cx="30" cy="35" r="4" fill="#111" className="vbs-g-node vbs-g-node-1" />
          <circle cx="115" cy="40" r="4" fill="#111" className="vbs-g-node vbs-g-node-2" />
          <circle cx="35" cy="110" r="4" fill="#111" className="vbs-g-node vbs-g-node-3" />
          <circle cx="110" cy="115" r="4" fill="#111" className="vbs-g-node vbs-g-node-4" />
          <circle cx="70" cy="70" r="6" fill="#111" />
        </svg>
      );

    case "space":
      return (
        <svg {...common} className="vbs-glyph vbs-g-space">
          <g className="vbs-g-cube">
            <polygon points="70,25 115,50 115,100 70,125 25,100 25,50" />
            <line x1="70" y1="25" x2="70" y2="75" />
            <line x1="25" y1="50" x2="70" y2="75" />
            <line x1="115" y1="50" x2="70" y2="75" />
            <line x1="70" y1="75" x2="70" y2="125" />
          </g>
        </svg>
      );

    case "health":
      return (
        <svg {...common} className="vbs-glyph vbs-g-health">
          <line x1="15" y1="70" x2="125" y2="70" stroke="rgba(0,0,0,0.12)" />
          <polyline
            className="vbs-g-pulse"
            points="15,70 40,70 50,55 60,90 70,40 80,95 90,70 125,70"
          />
        </svg>
      );

    case "assist":
      return (
        <svg {...common} className="vbs-glyph vbs-g-assist">
          <circle cx="70" cy="70" r="5" fill="#111" />
          <circle cx="70" cy="30" r="4" fill="#111" className="vbs-g-orbit vbs-g-orbit-1" />
          <circle cx="110" cy="70" r="4" fill="#111" className="vbs-g-orbit vbs-g-orbit-2" />
          <circle cx="70" cy="110" r="4" fill="#111" className="vbs-g-orbit vbs-g-orbit-3" />
          <circle cx="30" cy="70" r="4" fill="#111" className="vbs-g-orbit vbs-g-orbit-4" />
          <circle cx="70" cy="70" r="44" stroke="rgba(0,0,0,0.12)" />
        </svg>
      );

    case "live":
    default:
      return (
        <svg {...common} className="vbs-glyph vbs-g-live">
          <circle cx="70" cy="70" r="50" stroke="rgba(0,0,0,0.12)" />
          <circle cx="70" cy="70" r="50" className="vbs-g-live-ring" />
          <path d="M52 72 L65 86 L92 56" className="vbs-g-live-tick" />
        </svg>
      );
  }
};

const VisionBootSequence = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { planId, durationInYears, category, next } = state || {};

  const [activeStep, setActiveStep] = useState(0);
  const [done, setDone] = useState(false);
  const [exiting, setExiting] = useState(false);
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (!planId) navigate("/dashboard/start", { replace: true });
  }, [planId, navigate]);

  useEffect(() => {
    if (!planId) return;
    if (activeStep >= STEPS.length - 1) {
      setDone(true);
      const exitTimer = setTimeout(() => setExiting(true), FINAL_HOLD_MS);
      const navTimer = setTimeout(() => {
        if (navigatedRef.current) return;
        navigatedRef.current = true;
        navigate(next || `/dashboard/year-plan/${planId}`, { replace: true });
      }, FINAL_HOLD_MS + EXIT_MS);
      return () => {
        clearTimeout(exitTimer);
        clearTimeout(navTimer);
      };
    }
    const t = setTimeout(() => setActiveStep((s) => s + 1), STEP_MS);
    return () => clearTimeout(t);
  }, [activeStep, planId, navigate, next]);

  if (!planId) return null;

  const current = STEPS[activeStep];
  const pct = Math.min(100, ((activeStep + 1) / STEPS.length) * 100);

  return (
    <div className={`vbs-page ${exiting ? "vbs-exiting" : ""}`}>
      <div className="vbs-bg" aria-hidden="true">
        <div className="vbs-grid" />
      </div>

      <div className="vbs-stage">
        <div className="vbs-counter">
          <span className="vbs-counter-num">{String(activeStep + 1).padStart(2, "0")}</span>
          <span className="vbs-counter-sep">/</span>
          <span className="vbs-counter-total">{String(STEPS.length).padStart(2, "0")}</span>
        </div>

        <div className="vbs-glyph-stage">
          <Glyph key={current.key} stepKey={current.key} />
        </div>

        <p className="vbs-eyebrow">
          {done ? "Ready" : `Building Vision · ${durationInYears === 5 ? "P5" : `${durationInYears}Y`}`}
        </p>

        <h1 className="vbs-heading" key={current.key}>
          {done ? "Welcome inside." : current.label}
        </h1>

        <p className="vbs-detail" key={`d-${current.key}`}>
          {done
            ? "Your Vision is on the record. Start documenting."
            : current.detail}
        </p>

        <ul className="vbs-checklist">
          {STEPS.slice(0, -1).map((s, idx) => {
            const status =
              idx < activeStep ? "done" : idx === activeStep ? "active" : "pending";
            return (
              <li key={s.key} className={`vbs-item vbs-item-${status}`}>
                <span className="vbs-item-dot" />
                <span className="vbs-item-label">{s.label}</span>
                <span className="vbs-item-meta">
                  {status === "done" ? "ok" : status === "active" ? "···" : ""}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="vbs-progress">
          <div className="vbs-progress-bar" style={{ width: `${pct}%` }} />
          <div className="vbs-progress-shimmer" style={{ left: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

export default VisionBootSequence;
